import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import PaywallBanner from "../components/PaywallBanner";
import { PRICING } from "../config/pricing";
import { formatCurrency } from "../config/locale";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort, formatEuroAmount } from "../lib/currency";

const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  trialing: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  past_due: "bg-amber-50 text-amber-800 ring-amber-100",
  canceled: "bg-rose-50 text-rose-700 ring-rose-100",
  incomplete: "bg-slate-100 text-slate-700 ring-slate-200",
  unpaid: "bg-rose-50 text-rose-700 ring-rose-100",
  none: "bg-slate-100 text-slate-700 ring-slate-200",
};

export default function Subscription() {
  const { t, i18n } = useTranslation();
  const { user, organization, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [stripeConfigured, setStripeConfigured] = useState(true);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const locale = i18n.language;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("success")) toast.success(t("subscription.success_toast"));
    if (params.get("canceled")) toast.info(t("subscription.canceled_toast"));
    if (params.get("success")) refresh();
  }, [location.search, t, refresh]);

  const showTeamCallout = new URLSearchParams(location.search).get("success") === "1";

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([
          api.get("/billing/plans"),
          api.get("/billing/subscription"),
        ]);
        setPlans(p.data.plans || []);
        setStripeConfigured(!!p.data.stripe_configured);
        setSub(s.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const hasActive = sub && (sub.status === "active" || sub.status === "trialing");

  const startCheckout = async (plan) => {
    setCheckoutPlan(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan });
      window.location.assign(data.checkout_url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "STRIPE_NOT_CONFIGURED") {
        toast.error(t("subscription.stripe_not_configured"));
      } else {
        toast.error(formatApiErrorDetail(detail) || t("common.error_generic"));
      }
    } finally {
      setCheckoutPlan(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal");
      window.location.assign(data.portal_url);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "STRIPE_NOT_CONFIGURED") {
        toast.error(t("subscription.stripe_not_configured"));
      } else {
        toast.error(formatApiErrorDetail(detail) || t("common.error_generic"));
      }
    }
  };

  const seatText = useMemo(
    () => t("subscription.seat_usage", { used: sub?.seats_used ?? 0, included: sub?.seats_included ?? 3 }),
    [t, sub]
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscription-page">
      <PaywallBanner />
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("subscription.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl" data-testid="subscription-title">
            {t("subscription.title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("subscription.subtitle")}</p>
        </div>

        {showTeamCallout && hasActive && (
          <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm" data-testid="sub-success-team-callout">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-emerald-900">{t("subscription.success_callout_title")}</p>
                <p className="mt-1 text-sm text-emerald-800">{t("subscription.success_callout_body")}</p>
              </div>
              <Link to="/settings/team" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 whitespace-nowrap" data-testid="sub-success-invite-team">
                {t("subscription.success_callout_cta")}
              </Link>
            </div>
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">{t("common.loading")}</div>
        ) : hasActive ? (
          <>
            {sub.promo_active && (
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 p-5 shadow-sm" data-testid="sub-promo-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800">🌞 {t("promo.summer_label")}</p>
                    <p className="mt-1 text-lg font-semibold text-emerald-900" data-testid="sub-promo-title">
                      {t("subscription.promo_title", { date: formatDateShort(sub.promo_end_date) })}
                    </p>
                    <p className="mt-1 text-sm text-emerald-800">{t("subscription.promo_body")}</p>
                  </div>
                </div>
              </div>
            )}
            {sub.is_trial && !sub.promo_active && (
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm" data-testid="sub-trial-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800">{t("subscription.trial_eyebrow")}</p>
                    <p className="mt-1 text-lg font-semibold text-emerald-900" data-testid="sub-trial-title">
                      {t("subscription.trial_title", { days: sub.trial_days_remaining ?? 0 })}
                    </p>
                    <p className="mt-1 text-sm text-emerald-800">
                      {t("subscription.trial_body", { date: formatDateShort(sub.trial_ends_at) })}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sub-current-card">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("subscription.current_plan")}</p>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ring-1 ${STATUS_TONE[sub.status] || STATUS_TONE.none}`} data-testid="sub-status-badge">
                  {t(`subscription.status_${sub.status}`)}
                </span>
              </div>
              <p className="mt-2 text-2xl font-semibold text-slate-900" data-testid="sub-plan-name">
                {sub.plan === "annual" ? t("subscription.plan_annual") : t("subscription.plan_monthly")}
              </p>
              <p className="mt-1 text-sm text-slate-600" data-testid="sub-plan-price">
                {formatCurrency(sub.plan === "annual" ? PRICING.annual.amount_usd : PRICING.monthly.amount_usd)}
                {" · "}{t(`subscription.billed_${sub.plan}`)}
              </p>
              <dl className="mt-4 space-y-2 text-sm text-slate-700">
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("subscription.renewal_date")}</dt>
                  <dd data-testid="sub-renewal-date">{formatDateShort(sub.current_period_end)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("subscription.seats")}</dt>
                  <dd data-testid="sub-seat-usage">{seatText}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500">{t("subscription.source")}</dt>
                  <dd className="text-xs uppercase tracking-wider text-slate-500">{sub.source}</dd>
                </div>
              </dl>
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  onClick={openPortal}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="sub-portal-btn"
                >
                  {t("subscription.manage_portal")}
                </button>
                <button
                  onClick={openPortal}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="sub-change-plan-btn"
                >
                  {t("subscription.change_plan")}
                </button>
                <Link to="/settings/team" className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700" data-testid="sub-manage-team-btn">
                  {t("subscription.manage_team")}
                </Link>
              </div>
              {!stripeConfigured && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="sub-override-note">
                  {t("subscription.admin_override_hint")}
                </p>
              )}
            </div>

            <InvoicesCard invoices={sub.invoices} locale={locale} t={t} />
            </div>
            {sub.is_trial && (
              <div className="mt-6 grid gap-6 md:grid-cols-2" data-testid="sub-trial-plans-grid">
                <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("subscription.trial_pick_plan")}
                </p>
                {plans.map((p) => (
                  <PlanCard
                    key={p.id}
                    plan={p}
                    locale={locale}
                    busy={checkoutPlan === p.id}
                    disabled={!stripeConfigured || checkoutPlan !== null}
                    onSubscribe={() => startCheckout(p.id)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="grid gap-6 md:grid-cols-2" data-testid="sub-plan-grid">
            {!stripeConfigured && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 md:col-span-2" data-testid="sub-stripe-notice">
                {t("subscription.stripe_not_configured_body")}
              </div>
            )}
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                locale={locale}
                busy={checkoutPlan === p.id}
                disabled={!stripeConfigured || checkoutPlan !== null}
                onSubscribe={() => startCheckout(p.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function PlanCard({ plan, locale, busy, disabled, onSubscribe }) {
  const { t } = useTranslation();
  const isAnnual = plan.id === "annual";
  const savingsBadge = isAnnual ? t("subscription.savings_badge") : null;
  return (
    <div
      className={`relative rounded-2xl border ${isAnnual ? "border-indigo-300" : "border-slate-200"} bg-white p-6 shadow-sm`}
      data-testid={`plan-card-${plan.id}`}
    >
      {savingsBadge && (
        <span className="absolute -top-2 right-4 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          {savingsBadge}
        </span>
      )}
      <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
        {locale === "fr" ? plan.label_fr : plan.label_en}
      </p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900" data-testid={`plan-price-${plan.id}`}>
        {formatEuroAmount(plan.amount_usd || plan.amount_eur, locale)}
        <span className="ml-1 text-sm font-normal text-slate-500">
          / {t(`subscription.interval_${plan.interval}`)}
        </span>
      </p>
      {isAnnual && (
        <p className="mt-1 text-xs text-slate-500" data-testid="plan-monthly-equiv">
          {t("subscription.annual_monthly_equiv")}
        </p>
      )}
      <p className="mt-2 text-sm text-slate-600">
        {locale === "fr" ? plan.description_fr : plan.description_en}
      </p>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
        <li className="flex gap-2"><span className="text-indigo-600">✓</span>{t("subscription.feat_seats")}</li>
        <li className="flex gap-2"><span className="text-indigo-600">✓</span>{t("subscription.feat_submissions")}</li>
        <li className="flex gap-2"><span className="text-indigo-600">✓</span>{t("subscription.feat_marketplace")}</li>
        <li className="flex gap-2"><span className="text-indigo-600">✓</span>{t("subscription.feat_buyer")}</li>
        <li className="flex gap-2"><span className="text-indigo-600">✓</span>{t("subscription.feat_cancel")}</li>
      </ul>
      <button
        onClick={onSubscribe}
        disabled={disabled}
        className="mt-6 w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
        data-testid={`plan-subscribe-${plan.id}`}
      >
        {busy ? t("common.loading") : t(`subscription.choose_${plan.id}`)}
      </button>
    </div>
  );
}

function InvoicesCard({ invoices, locale, t }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sub-invoices-card">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("subscription.invoices")}</p>
      {!invoices || invoices.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500" data-testid="sub-invoices-empty">{t("subscription.invoices_empty")}</p>
      ) : (
        <table className="mt-4 w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="py-2 text-left">{t("subscription.inv_date")}</th>
              <th className="py-2 text-right">{t("subscription.inv_amount")}</th>
              <th className="py-2 text-left">{t("subscription.inv_status")}</th>
              <th className="py-2 text-right">{t("subscription.inv_link")}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100" data-testid={`inv-row-${inv.id}`}>
                <td className="py-2 text-slate-700">{formatDateShort(inv.created_at)}</td>
                <td className="py-2 text-right text-slate-900">{formatEuroAmount(inv.amount_eur || (inv.amount_cents || 0) / 100, locale)}</td>
                <td className="py-2 text-slate-600">{inv.status || "—"}</td>
                <td className="py-2 text-right">
                  {inv.hosted_invoice_url ? (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">{t("subscription.inv_view")}</a>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
